/**
 * Client-side image compression helpers.
 * Prevents TWA Android WebView freezes caused by native camera photos (8-12 MB).
 * Downscales + re-encodes as JPEG before uploading.
 */

/**
 * Compress an image File/Blob. Resolves to a Blob (JPEG).
 * Rejects if the file is not a loadable image.
 *
 * @param {File|Blob} file - source image
 * @param {number} maxDim - max width/height in px (default 1920)
 * @param {number} quality - JPEG quality 0..1 (default 0.85)
 * @returns {Promise<Blob>}
 */
export async function compressImage(file, maxDim = 1920, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const ratio = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('canvas blob null'))),
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image load failed'));
    };
    img.src = url;
  });
}

/**
 * Safely compress: returns the original file if not an image or if compression fails.
 * Use this wrapper in upload handlers that may receive non-image files.
 *
 * @param {File|Blob} file
 * @param {number} maxDim
 * @param {number} quality
 * @returns {Promise<File|Blob>}
 */
export async function compressImageSafe(file, maxDim = 1920, quality = 0.85) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  try {
    return await compressImage(file, maxDim, quality);
  } catch {
    return file;
  }
}
